import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useParams, useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Menu from '@mui/material/Menu';
import TabList from '@mui/lab/TabList';
import Alert from '@mui/material/Alert';
import TabPanel from '@mui/lab/TabPanel';
import Button from '@mui/material/Button';
import TabContext from '@mui/lab/TabContext';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import ListItemIcon from '@mui/material/ListItemIcon';

import { useAssignedRiskApplicationContext } from 'src/api/hooks/useAssignedRiskApplicationQueries';
import { MESSAGES } from 'src/constants/messages';

import DynamicBreadcrumbs from 'src/sections/employer-data/dynamic-breadcrumbs/dynamic-breadcrumbs';

import AgentDetails from './agent-details/agent-details';
import InsuranceHistory from './insurance-history/insurance-history';
import NamesAndAddresses from './names-and-addresses/names-and-addresses';
import ApplicationDetails from './application-details/application-details';
import CoverageInformation from './coverage-information/coverage-information';

const applicationMessages = MESSAGES.ASSIGNED_RISK_APPLICATION;

const tabs = [
  ['coverage-information', applicationMessages.TABS.COVERAGE_INFORMATION],
  ['application-details', applicationMessages.TABS.APPLICATION_DETAILS],
  ['names-and-addresses', applicationMessages.TABS.NAMES_AND_ADDRESSES],
  ['premiums', applicationMessages.TABS.PREMIUMS],
  ['insurance-history', applicationMessages.TABS.INSURANCE_HISTORY],
  ['documents', applicationMessages.TABS.DOCUMENTS],
  ['error-summary', applicationMessages.TABS.ERROR_SUMMARY],
  ['agent-details', applicationMessages.TABS.AGENT_DETAILS],
  ['history', applicationMessages.TABS.HISTORY],
] as const;

const applicationActions = [
  { label: applicationMessages.ACTIONS.ACCEPT, icon: 'lucide:check' },
  { label: applicationMessages.ACTIONS.PEND, icon: 'lucide:pause' },
  { label: applicationMessages.ACTIONS.DECLINE, icon: 'lucide:x' },
  { label: applicationMessages.ACTIONS.MOVE, icon: 'lucide:folder-input' },
];

function renderTabContent(value: string, label: string, assignedRiskAppId: number) {
  switch (value) {
    case 'coverage-information':
      return <CoverageInformation assignedRiskAppId={assignedRiskAppId} />;
    case 'application-details':
      return <ApplicationDetails assignedRiskAppId={assignedRiskAppId} />;
    case 'names-and-addresses':
      return <NamesAndAddresses assignedRiskAppId={assignedRiskAppId} />;
    case 'insurance-history':
      return <InsuranceHistory assignedRiskAppId={assignedRiskAppId} />;
    case 'agent-details':
      return <AgentDetails assignedRiskAppId={assignedRiskAppId} />;
    default:
      return (
        <Alert severity="info" data-testid={`ar-tab-unavailable-${value}`}>
          {applicationMessages.TAB_UNAVAILABLE(label)}
        </Alert>
      );
  }
}

export default function AssignedRiskApplicationPage() {
  const { assignedRiskAppId: assignedRiskAppIdParam } = useParams<{ assignedRiskAppId: string }>();
  const assignedRiskAppId = Number(assignedRiskAppIdParam);
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = (location.state as { tab?: string } | null)?.tab;
  const initialTab = tabs.some(([value]) => value === requestedTab) ? requestedTab! : tabs[0][0];
  const [tab, setTab] = useState(initialTab);
  const [visitedTabs, setVisitedTabs] = useState([initialTab]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const { data, isLoading, isError } = useAssignedRiskApplicationContext(assignedRiskAppId);
  const context = data?.data;

  if (isError || (!isLoading && !context)) return <Alert severity="error">{applicationMessages.NOT_FOUND}</Alert>;

  const applicationName = context?.applicationName ?? applicationMessages.DEFAULT_NAME;
  const applicationStatus = context?.appStatusDescription ?? applicationMessages.UNKNOWN_STATUS;
  const isActionMenuOpen = Boolean(actionMenuAnchor);

  return (
    <Box data-testid="assigned-risk-application-page">
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isLoading ? (
                <Skeleton variant="text" width={280} height={48} data-testid="assigned-risk-application-loading" />
              ) : (
                <>
                  <Typography variant="h4">{applicationName}</Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 700 }}
                    data-testid="assigned-risk-application-status"
                  >
                    {applicationStatus}
                  </Typography>
                </>
              )}
            </Box>
            <ButtonGroup
              variant="outlined"
              aria-label="assigned risk application actions"
              sx={{
                '--ar-action-border': 'rgba(145, 158, 171, 0.12)',
                height: 48,
                borderRadius: 1,
                border: '1px solid var(--ar-action-border)',
                '& .MuiButton-root, & .MuiIconButton-root': {
                  height: 48,
                  border: '1px solid var(--ar-action-border)',
                },
                '& .MuiButton-root': {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                '& .MuiIconButton-root': {
                  minWidth: 48,
                  p: 1,
                  borderLeft: '1px solid var(--ar-action-border)',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 8,
                  borderBottomRightRadius: 8,
                },
              }}
            >
                <Button
                startIcon={<Icon icon="lucide:check" width={20} />}
                  disabled
                data-testid="assigned-risk-application-accept"
              >
                Accept
              </Button>
              <IconButton
                aria-label="open assigned risk application actions"
                aria-controls={isActionMenuOpen ? 'assigned-risk-application-actions' : undefined}
                aria-haspopup="menu"
                aria-expanded={isActionMenuOpen ? 'true' : undefined}
                onClick={(event) => setActionMenuAnchor(event.currentTarget)}
                data-testid="assigned-risk-application-actions-button"
              >
                <Icon icon="lucide:chevron-down" width={20} />
              </IconButton>
            </ButtonGroup>
            <Menu
              id="assigned-risk-application-actions"
              anchorEl={actionMenuAnchor}
              open={isActionMenuOpen}
              onClose={() => setActionMenuAnchor(null)}
              slotProps={{ list: { 'aria-label': 'assigned risk application actions' } }}
            >
              {applicationActions.map((action) => (
                <MenuItem key={action.label} disabled>
                  <ListItemIcon>
                    <Icon icon={action.icon} width={20} />
                  </ListItemIcon>
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <DynamicBreadcrumbs crumbStartPath="combo" />
          <TabList
            onChange={(_event, value) => {
              setTab(value);
              navigate(location.pathname, {
                state: { ...(location.state as Record<string, unknown> | null), tab: value },
                replace: true,
              });
              setVisitedTabs((previousTabs) => previousTabs.includes(value) ? previousTabs : [...previousTabs, value]);
            }}
            aria-label="assigned risk application tabs"
          >
            {tabs.map(([value, label]) => <Tab key={value} value={value} label={label} data-testid={`ar-tab-${value}`} />)}
          </TabList>
        </Box>
        {tabs.map(([value, label]) => visitedTabs.includes(value) ? (
          <TabPanel key={value} value={value} keepMounted sx={{ px: 0 }}>
            {renderTabContent(value, label, assignedRiskAppId)}
          </TabPanel>
        ) : null)}
      </TabContext>
    </Box>
  );
}
